import { getToolResultData } from "./types/tool-result";
import type { ChatUIMessage } from "./types/chat-ui-message";

type ChatLikeMessage = {
  role?: unknown;
  parts?: unknown;
};

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readInputAgentId(part: unknown): string | undefined {
  if (!isObject(part) || !isObject(part.input)) {
    return undefined;
  }

  const candidate = part.input.agentId;
  return typeof candidate === "string" ? candidate : undefined;
}

function isRunSubAgentPart(part: unknown): boolean {
  if (!isObject(part)) {
    return false;
  }

  if (part.type === "tool-runSubAgent") {
    return true;
  }

  return part.type === "dynamic-tool" && part.toolName === "runSubAgent";
}

function isRunningState(part: unknown): boolean {
  if (!isObject(part)) {
    return false;
  }

  return part.state === "input-streaming" || part.state === "input-available";
}

function hasRunningNestedSubAgentTools(output: unknown): boolean {
  const resolvedOutput = getToolResultData(output) ?? output;
  if (!isObject(resolvedOutput) || !Array.isArray(resolvedOutput.parts)) {
    return false;
  }

  for (const nestedPart of resolvedOutput.parts) {
    if (!isObject(nestedPart)) {
      continue;
    }

    if (nestedPart.type !== "dynamic-tool") {
      continue;
    }

    if (isRunningState(nestedPart)) {
      return true;
    }
  }

  return false;
}

/**
 * Detects whether the latest assistant turn currently has an active policy-review
 * runSubAgent invocation, even if persisted agent state has not updated yet.
 */
export function hasRunningPolicyReviewSubAgent(
  messages: ChatLikeMessage[]
): boolean {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.role !== "assistant") {
      continue;
    }

    const parts = Array.isArray(message.parts) ? message.parts : [];
    for (let partIndex = parts.length - 1; partIndex >= 0; partIndex -= 1) {
      const part = parts[partIndex];
      if (!isRunSubAgentPart(part)) {
        continue;
      }

      const isPolicyReviewAgent = readInputAgentId(part) === "policy-review";
      if (!isPolicyReviewAgent) {
        continue;
      }

      if (isRunningState(part)) {
        return true;
      }

      if (isObject(part) && hasRunningNestedSubAgentTools(part.output)) {
        return true;
      }
    }

    // Only evaluate the latest assistant turn to avoid stale activation from history.
    return false;
  }

  return false;
}

function readTimestamp(value: unknown): number | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function getLatestPolicyReviewContextUpdatedAt(
  messages: ChatUIMessage[],
  clearedAt?: string
): string | undefined {
  const clearedTime = readTimestamp(clearedAt);

  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex -= 1) {
    const message = messages[messageIndex];
    if (!message) {
      continue;
    }
    const parts = Array.isArray(message.parts) ? message.parts : [];

    for (let partIndex = parts.length - 1; partIndex >= 0; partIndex -= 1) {
      const part = parts[partIndex] as {
        type?: unknown;
        data?: {
          subAgentId?: unknown;
          updatedAt?: unknown;
        };
      };

      if (
        part.type !== "data-subAgentContext" ||
        part.data?.subAgentId !== "policy-review" ||
        typeof part.data?.updatedAt !== "string"
      ) {
        continue;
      }

      const contextTime = readTimestamp(part.data.updatedAt);
      if (
        contextTime !== undefined &&
        clearedTime !== undefined &&
        contextTime <= clearedTime
      ) {
        return undefined;
      }

      return part.data.updatedAt;
    }
  }

  return undefined;
}
